import React, { useState, useEffect, useRef } from "react";
import { SanityCommunityfundApplicationForm, SanityDynamicForm } from "@graphql-types";
import { Heading, Button, BlockContent, Image } from "@global";
import styled from "styled-components";
import useFormProgressStore from "@state/formProgressStore";
import {
  marginBottom,
  HEADER_HEIGHT,
  colors,
  FORM_UPLOAD_MAX_FILES_PER_FIELD,
  FORM_USE_FIREBASE_UPLOADS,
} from "@util/constants";
import { isFieldHidden } from "@util/helper";
import { generateFormPDF } from "@util/pdfGenerator";
import { sendEmail } from "../../../functions/sendEmail";
import FormFields from "./formFields";
import type { PermitFormField } from "./formFields";
import {
  makeUploadId,
  uploadFileToFirebase,
  validateUploadFile,
  type UploadedFileRecord,
} from "@util/fileUploads";
import { captureMonitoringError } from "@util/monitoring";
import { getFieldKey } from "@util/formUtils";

/** Form data for dynamic forms (from generated GraphQL types) */
export type DynamicFormData = SanityCommunityfundApplicationForm | SanityDynamicForm;

const NETLIFY_DEFAULT_FORM_NAME = "dynamic-form";

const IS_DEV = process.env.NODE_ENV === "development";

/** Build FormData for Netlify Forms submit (file values are link metadata, no binary file upload). */
function buildNetlifyFormData(
  formInputs: Record<string, unknown>,
  formId: string,
  uploadedFilesByField: Record<string, UploadedFileRecord[]>,
  pdfFile?: File,
): FormData {
  const fd = new FormData();
  fd.append("form-name", formId || NETLIFY_DEFAULT_FORM_NAME);
  fd.append("form_id", formId);
  fd.append("bot-field", ""); // honeypot

  // Keep generated submission PDF attached
  if (pdfFile) {
    fd.append("form-submission-pdf", pdfFile);
  }

  const fileManifest: Array<{
    field: string;
    fileName: string;
    size: number;
    mimeType: string;
    url: string;
    storagePath?: string;
  }> = [];

  for (const [key, value] of Object.entries(formInputs)) {
    if (key.endsWith("Error")) continue;
    const uploadedForField = (uploadedFilesByField[key] || []).filter(
      file => file.status === "uploaded" && file.downloadUrl,
    );
    if (uploadedForField.length > 0) {
      fd.append(key, uploadedForField.map(file => file.downloadUrl).join("\n"));
      uploadedForField.forEach(file => {
        fileManifest.push({
          field: key,
          fileName: file.fileName,
          size: file.size,
          mimeType: file.mimeType,
          url: file.downloadUrl || "",
          storagePath: file.storagePath,
        });
      });
      continue;
    }
    if (Array.isArray(value)) {
      // Serialize as a JSON string so Netlify stores one value per key and
      // downstream consumers (generate-pdfs.mjs, sendEmail) can parse it back.
      // Multiple fd.append calls for the same key are unreliable in Netlify Forms,
      // and String(obj) loses object data entirely for table rows.
      fd.append(key, JSON.stringify(value));
    } else if (value !== undefined && value !== null) {
      fd.append(key, String(value));
    }
  }

  if (fileManifest.length > 0) {
    fd.append("uploaded_file_manifest", JSON.stringify(fileManifest));
  }
  return fd;
}

export const DynamicFormWrapper = styled.div`
  width: 100%;
  .form-header {
    ${marginBottom.m}
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 64px;
    margin-bottom: 64px;
  }

  .form-section-title {
    position: sticky;
    top: calc(${HEADER_HEIGHT}px - 2px);
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0;
    margin-bottom: 0.5rem;
    background: ${colors.lightGrey};
    border-bottom: 1px solid ${colors.border};
  }

  .form-section-title-content {
    flex: 1;
    min-width: 0;
  }

  .form-section-save {
    flex-shrink: 0;
  }

  .form-section-save button {
    background: none;
    border: none;
    padding: 0;
    min-height: unset;
    color: ${colors.flightBlue};
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    border-radius: 0;
  }

  .form-section-save button:hover {
    text-decoration: underline;
  }

  .form-section-save button::after {
    display: none;
  }

  .form-fields {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .form-field.full-width {
    grid-column: span 2;

    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }

  .form-field.half-width {
    grid-column: span 1;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-field label {
    font-weight: 500;
  }

  .form-field .form-field-required {
    color: #e74c3c;
  }

  .form-field .char-count {
    font-size: 0.875rem;
    color: #555;
    text-align: right;
    line-height: 1.2;
  }

  .form-field .char-count.over {
    color: #e74c3c;
  }

  .form-field input,
  .form-field textarea,
  .form-field select {
    padding: 0.75rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 1rem;
    width: 100%;
  }

  .form-field .amount-input {
    position: relative;
    width: 100%;
  }

  .form-field .amount-prefix {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #b0b0b0;
    font-size: 0.9rem;
    line-height: 1;
    font-weight: 600;
    pointer-events: none;
    white-space: nowrap;
  }

  .form-field .amount-input input {
    padding-left: 3.25rem;
  }

  .form-field textarea {
    min-height: 100px;
    resize: vertical;
  }

  .form-field select {
    cursor: pointer;
  }

  .form-field input[type="file"] {
    padding: 0.5rem 0;
  }

  .form-field .form-field-file-name {
    font-size: 0.875rem;
    color: #555;
  }

  /* Error state: red border on wrapper's inputs (class + data attribute for reliability) */
  .form-field.error input,
  .form-field.error textarea,
  .form-field.error select,
  .form-field[data-error="true"] input,
  .form-field[data-error="true"] textarea,
  .form-field[data-error="true"] select {
    border: 2px solid #e74c3c !important;
    outline: none;
  }

  /* Fallback: inputs with .error class (Field also applies this to the control) */
  .form-field input.error,
  .form-field textarea.error,
  .form-field select.error {
    border: 2px solid #e74c3c !important;
    outline: none;
  }

  .form-field .error-message {
    color: #e74c3c;
    font-size: 0.875rem;
  }

  .checkbox-group,
  .radio-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .checkbox-item,
  .radio-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .checkbox-item input,
  .radio-item input {
    width: auto;
  }

  .form-submit {
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    align-items: center;
  }

  .form-message {
    padding: 1rem;
    border-radius: 4px;
    margin-top: 1rem;
  }

  .form-message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .form-message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .form-message.warning {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
  }

  .pdf-viewer-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .pdf-viewer-content {
    background: white;
    width: 100%;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    overflow: hidden;
  }

  .pdf-viewer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #ccc;
    background: ${colors.lightGrey};
  }

  .pdf-viewer-header h3 {
    margin: 0;
    font-size: 1.25rem;
  }

  .pdf-viewer-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pdf-viewer-close:hover {
    color: #000;
  }

  .pdf-viewer-iframe {
    width: 100%;
    height: calc(90vh - 80px);
    border: none;
  }
`;

interface DynamicFormViewProps {
  formData: DynamicFormData;
}

/** Stable id for persisting this form's progress (store key). */
function getFormId(formData: DynamicFormData): string {
  return formData._id ?? `form-${formData._type ?? "dynamic"}`;
}

/** Shared view for dynamic forms (sections + form fields + submit). Used by CommunityFundApplicationForm and DynamicForm. */
function DynamicFormView({ formData }: DynamicFormViewProps) {
  const formId = getFormId(formData);
  const cmsFormID = formData.formID;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formInputsFromStore = useFormProgressStore((state: any) => state.byFormId[formId] ?? {});
  const [localOverlay, setLocalOverlay] = useState<Record<string, unknown>>({});
  const [savedSectionKey, setSavedSectionKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "warning" | "error";
    text: string;
  } | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fileUploadsByField, setFileUploadsByField] = useState<Record<string, UploadedFileRecord[]>>(
    {},
  );
  const fileBlobRef = useRef<Record<string, File>>({});

  const handleSaveProgress = (sectionKey: string) => {
    setSavedSectionKey(sectionKey);
    setTimeout(() => setSavedSectionKey(null), 5000);
  };

  const formInputs = { ...formInputsFromStore, ...localOverlay };
  const formInputsWithUploads = { ...formInputs, ...fileUploadsByField };

  const sections = (formData.section ?? []).filter(Boolean);
  const allFormFields = sections.flatMap(s =>
    (s?.formFields ?? []).filter((f): f is NonNullable<typeof f> => f != null),
  );

  const updateField = useFormProgressStore.getState().updateField;
  const clearForm = useFormProgressStore.getState().clearForm;

  const getUploadedFilesForSubmit = (
    currentByField: Record<string, UploadedFileRecord[]>,
  ): Record<string, UploadedFileRecord[]> => {
    const out: Record<string, UploadedFileRecord[]> = {};
    Object.entries(currentByField).forEach(([key, files]) => {
      out[key] = files.filter(file => file.status === "uploaded" && Boolean(file.downloadUrl));
    });
    return out;
  };

  const setFileFieldError = (key: string, message: string | null) => {
    setLocalOverlay(prev => {
      const next = { ...prev };
      if (message) {
        next[`${key}Error`] = message;
      } else {
        delete next[`${key}Error`];
      }
      return next;
    });
  };

  const upsertUploadRecord = (key: string, record: UploadedFileRecord) => {
    setFileUploadsByField(prev => {
      const existing = prev[key] ?? [];
      const existingIndex = existing.findIndex(item => item.id === record.id);
      if (existingIndex === -1) {
        return { ...prev, [key]: [...existing, record] };
      }
      const next = [...existing];
      next[existingIndex] = { ...next[existingIndex], ...record };
      return { ...prev, [key]: next };
    });
  };

  const handleFileAdd = async (key: string, incomingFiles: File[]) => {
    if (!FORM_USE_FIREBASE_UPLOADS) {
      setFileFieldError(key, "File uploads are currently unavailable. Please try again shortly.");
      return;
    }

    const existingFiles = fileUploadsByField[key] ?? [];
    if (existingFiles.length + incomingFiles.length > FORM_UPLOAD_MAX_FILES_PER_FIELD) {
      setFileFieldError(
        key,
        `You can upload up to ${FORM_UPLOAD_MAX_FILES_PER_FIELD} files in this field.`,
      );
      return;
    }

    const dedupedIncoming = incomingFiles.filter(file => {
      const uploadId = makeUploadId(file);
      return !existingFiles.some(existing => existing.id === uploadId);
    });

    setFileFieldError(key, null);

    const queuedFiles = dedupedIncoming.map(file => {
      const uploadId = makeUploadId(file);
      fileBlobRef.current[uploadId] = file;
      upsertUploadRecord(key, {
        id: uploadId,
        fieldKey: key,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        status: "queued",
        progress: 0,
      });
      return { file, uploadId };
    });

    for (const { file, uploadId } of queuedFiles) {
      const validationError = validateUploadFile(file);
      if (validationError) {
        upsertUploadRecord(key, {
          id: uploadId,
          fieldKey: key,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
          status: "failed",
          progress: 0,
          error: validationError,
        });
        setFileFieldError(key, validationError);
        continue;
      }

      upsertUploadRecord(key, {
        id: uploadId,
        fieldKey: key,
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        status: "uploading",
        progress: 0,
      });

      try {
        const { url, storagePath } = await uploadFileToFirebase(formId, key, file, progress => {
          upsertUploadRecord(key, {
            id: uploadId,
            fieldKey: key,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            status: "uploading",
            progress,
          });
        });

        upsertUploadRecord(key, {
          id: uploadId,
          fieldKey: key,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
          status: "uploaded",
          progress: 100,
          downloadUrl: url,
          storagePath,
        });
      } catch (error) {
        captureMonitoringError(error, { location: "dynamicFormView.handleFileAdd", key, fileName: file.name });
        upsertUploadRecord(key, {
          id: uploadId,
          fieldKey: key,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
          status: "failed",
          progress: 0,
          error: error instanceof Error ? error.message : "Upload failed",
        });
        setFileFieldError(key, `Upload failed for ${file.name}. Please retry.`);
      }
    }
  };

  const handleFileRemove = (key: string, fileId: string) => {
    setFileUploadsByField(prev => {
      const nextFiles = (prev[key] ?? []).filter(file => file.id !== fileId);
      return { ...prev, [key]: nextFiles };
    });
    delete fileBlobRef.current[fileId];
    setFileFieldError(key, null);
  };

  const handleFileRetry = async (key: string, fileId: string) => {
    const file = fileBlobRef.current[fileId];
    if (!file) {
      setFileFieldError(key, "Could not retry this file. Please remove and re-upload it.");
      return;
    }
    await handleFileAdd(key, [file]);
  };

  const handleFieldChange = (key: string, value: unknown) => {
    updateField(formId, key, value);
    setLocalOverlay(prev => {
      const next = { ...prev };
      delete next[key];
      delete next[`${key}Error`];
      return next;
    });

    // Validate the field in real-time after updating
    const field = allFormFields.find(f => getFieldKey(f) === key);
    if (field) {
      const error = validateField(field as PermitFormField, value);
      setLocalOverlay(prev => {
        const next = { ...prev };
        if (error) {
          next[`${key}Error`] = error;
        } else {
          delete next[`${key}Error`];
        }
        return next;
      });
    }
    if (submitMessage) setSubmitMessage(null);
  };

  /** Check if a value is empty for the given field type */
  const isFieldValueEmpty = (value: unknown, fieldType: string | null | undefined): boolean => {
    if (value === undefined || value === null) return true;
    if (fieldType === "checkbox") {
      return !Array.isArray(value) || value.length === 0;
    }
    if (fieldType === "file") {
      if (!Array.isArray(value)) return true;
      return !value.some(v => {
        if (!v || typeof v !== "object") return false;
        const candidate = v as UploadedFileRecord;
        return candidate.status === "uploaded" && Boolean(candidate.downloadUrl);
      });
    }
    if (fieldType === "table") {
      if (!Array.isArray(value) || value.length === 0) return true;
      // Check if all rows are empty
      return value.every(row => {
        if (typeof row !== "object" || row === null) return true;
        return Object.values(row).every(cell => !cell || String(cell).trim() === "");
      });
    }
    if (typeof value === "string") return value.trim() === "";
    return false;
  };

  const hasSuspiciousHtml = (value: unknown): boolean => {
    if (typeof value !== "string") return false;
    return /<[^>]+>/.test(value);
  };

  const normalizeMaxCharacters = (value: unknown): number | undefined => {
    if (value === null || value === undefined) return undefined;
    if (typeof value !== "number") return undefined;
    if (!Number.isFinite(value)) return undefined;
    const int = Math.floor(value);
    if (int <= 0) return undefined;
    return int;
  };

  /** Validate a single field and return error message if invalid */
  const validateField = (
    field: PermitFormField | null | undefined,
    value: unknown,
  ): string | null => {
    if (!field || isFieldHidden(field, formInputsWithUploads)) return null;

    // Check required fields
    if (field.required && isFieldValueEmpty(value, field.type)) {
      return "This field is required";
    }

    // Block HTML-like input
    if (typeof value === "string" && value.trim() !== "" && hasSuspiciousHtml(value)) {
      return "Please remove HTML or special tags from this field";
    }

    const maxCharacters = normalizeMaxCharacters((field as any).maxCharacters);
    if (maxCharacters !== undefined && typeof value === "string" && value.length > maxCharacters) {
      return `Please limit this field to ${maxCharacters} characters`;
    }

    // Check email format
    if (
      field.type === "email" &&
      value &&
      typeof value === "string" &&
      value.trim() !== "" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ) {
      return "Please enter a valid email address";
    }

    // Check amount format
    if (field.type === "amount" && value && typeof value === "string" && value.trim() !== "") {
      const normalized = value.replace(/,/g, "").trim();
      if (normalized === "" || Number.isNaN(Number(normalized))) {
        return "Please enter a valid amount";
      }
    }

    // Validate table fields
    if (field.type === "table" && Array.isArray(value) && value.length > 0) {
      const columns = (field.tableColumns || []).filter(
        (col): col is NonNullable<typeof col> => col != null,
      );
      for (const row of value) {
        if (typeof row !== "object" || row === null) continue;
        for (const col of columns) {
          if (col.required && col.id) {
            const cellValue = row[col.id];
            if (!cellValue || String(cellValue).trim() === "") {
              return `Please fill in all required columns in the table`;
            }
            const colMaxCharacters = normalizeMaxCharacters((col as any).maxCharacters);
            if (
              colMaxCharacters !== undefined &&
              cellValue &&
              String(cellValue).length > colMaxCharacters
            ) {
              return `Please limit the ${col.label || col.id
                } column to ${colMaxCharacters} characters`;
            }
            // Validate email columns
            if (
              col.type === "email" &&
              cellValue &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(cellValue))
            ) {
              return `Please enter a valid email address in the ${col.label || col.id} column`;
            }
            if (cellValue && hasSuspiciousHtml(cellValue)) {
              return `Please remove HTML or special tags in the ${col.label || col.id} column`;
            }
            if (col.type === "amount" && cellValue) {
              const normalized = String(cellValue).replace(/,/g, "").replace(/^NZ\$/i, "").trim();
              if (normalized === "" || Number.isNaN(Number(normalized))) {
                return `Please enter a valid amount in the ${col.label || col.id} column`;
              }
            }
          }
        }
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    allFormFields.forEach(field => {
      if (isFieldHidden(field, formInputsWithUploads)) return;

      const fieldKey = getFieldKey(field);
      const value = formInputsWithUploads[fieldKey];
      const error = validateField(field as PermitFormField, value);

      if (error) {
        errors[`${fieldKey}Error`] = error;
      }
    });

    setLocalOverlay(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.endsWith("Error")) delete next[key];
      });
      Object.assign(next, errors);
      return next;
    });

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitMessage({
        type: "error",
        text: "Please fix the errors in the form before submitting.",
      });
      // Scroll after React has committed the error state (so .form-field.error exists in DOM)
      setTimeout(() => {
        const firstError = document.querySelector(
          '.form-field.error, .form-field[data-error="true"]',
        );
        firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const uploadedFilesForSubmit = getUploadedFilesForSubmit(fileUploadsByField);

      const formInputsForPDF = { ...formInputs };
      Object.entries(uploadedFilesForSubmit).forEach(([key, files]) => {
        formInputsForPDF[key] = files.map(file => file.fileName);
      });

      const hasFailedUploads = Object.values(fileUploadsByField).some(files =>
        files.some(file => file.status === "failed" || file.status === "uploading"),
      );
      if (hasFailedUploads) {
        setSubmitMessage({
          type: "error",
          text: "Please wait for all files to finish uploading or retry failed uploads.",
        });
        setIsSubmitting(false);
        return;
      }

      // Generate PDF first
      let pdfFile: File | undefined;

      console.log("formData", formData, formInputsForPDF);
      try {
        const pdfResult = generateFormPDF({
          formData: formData as {
            title?: string | null;
            section?: Array<{
              _key?: string | null;
              title?: any;
              formFields?: Array<PermitFormField | null> | null;
            } | null> | null;
          },
          formInputs: formInputsForPDF,
        });
        pdfFile = pdfResult.file;
        if (IS_DEV) window.open(pdfResult.blobUrl, "_blank");
      } catch (pdfError) {
        console.error("PDF generation error:", pdfError);
        captureMonitoringError(pdfError, { location: "dynamicFormView.generateFormPDF", formId });
        setSubmitMessage({
          type: "error",
          text: "There was an error generating the PDF. Please try again.",
        });
        setIsSubmitting(false);
        return;
      }

      // Submit form to Netlify with PDF attachment and file links.
      const formDataToSend = buildNetlifyFormData(
        formInputsForPDF,
        cmsFormID || formId,
        uploadedFilesForSubmit,
        pdfFile,
      );
      const action = typeof window !== "undefined" ? window.location.pathname || "/" : "/";
      const response = await fetch(action, {
        method: "POST",
        body: formDataToSend,
      });

      if (response.ok) {
        clearForm(formId);
        setLocalOverlay({});
        setFileUploadsByField({});
        fileBlobRef.current = {};
        if (!pdfFile) {
          setSubmitMessage({
            type: "warning",
            text: "Your form was submitted, but we could not generate your confirmation PDF.",
          });
          return;
        }

        // Await the email so failures are surfaced to the user
        try {
          const emailResult = await sendEmail(
            pdfFile,
            formData.title || "Form Submission",
            formData,
            formInputsForPDF,
            uploadedFilesForSubmit,
          );
          if (emailResult.success) {
            setSubmitMessage({
              type: "success",
              text: "Thank you! Your form has been submitted successfully.",
            });
          } else {
            console.error("Email delivery failed:", emailResult.error);
            setSubmitMessage({
              type: "warning",
              text: "Your form was saved, but we were unable to send the notification email. Please contact us directly to confirm your submission was received.",
            });
          }
        } catch (emailError) {
          console.error("Email sending failed:", emailError);
          captureMonitoringError(emailError, { location: "dynamicFormView.sendEmail", formId });
          setSubmitMessage({
            type: "warning",
            text: "Your form was saved, but we were unable to send the notification email. Please contact us directly to confirm your submission was received.",
          });
        }
      } else {
        setSubmitMessage({
          type: "error",
          text: "There was an error submitting your form. Please try again.",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      captureMonitoringError(error, { location: "dynamicFormView.handleSubmit", formId });
      setSubmitMessage({
        type: "error",
        text: "There was an error submitting your form. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clean up PDF URL when component unmounts or PDF is closed
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleClosePdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter") return;
    const target = event.target as HTMLElement | null;
    if (target?.tagName === "TEXTAREA") return;
    event.preventDefault();
  };

  return (
    <DynamicFormWrapper>
      {pdfUrl && (
        <div className="pdf-viewer-container" onClick={handleClosePdf}>
          <div className="pdf-viewer-content" onClick={e => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <h3>Form Submission PDF</h3>
              <button className="pdf-viewer-close" onClick={handleClosePdf} aria-label="Close PDF">
                ×
              </button>
            </div>
            <iframe src={pdfUrl} className="pdf-viewer-iframe" title="Form Submission PDF" />
          </div>
        </div>
      )}
      <div className="form-header">
        <Heading data={{ design: "h2", tag: "h2" }}>{formData.title}</Heading>
      </div>

      <form
        name={cmsFormID || NETLIFY_DEFAULT_FORM_NAME}
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        method="post"
        action="/"
        encType="multipart/form-data"
        onSubmit={handleSubmit}
        onKeyDown={handleFormKeyDown}
      >
        <input type="hidden" name="form-name" value={cmsFormID || NETLIFY_DEFAULT_FORM_NAME} />
        <input type="hidden" name="form_id" value={formId} />
        <p style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
          <label>
            Don’t fill this out: <input name="bot-field" />
          </label>
        </p>
        {sections.map(section => (
          <div className="form-section" key={section?._key}>
            <div className="form-section-title">
              <div className="form-section-title-content">
                <BlockContent data={section?.title} />
              </div>
              <div className="form-section-save">
                <Button
                  type="button"
                  text={
                    savedSectionKey === section?._key
                      ? "Progress saved. Revisit this page from the same browser to continue"
                      : "Save progress"
                  }
                  theme="base"
                  onClick={() => section?._key && handleSaveProgress(section._key)}
                />
              </div>
            </div>
            <FormFields
              fields={(section?.formFields ?? []).filter(Boolean) as PermitFormField[]}
              inputs={formInputsWithUploads}
              onChange={handleFieldChange}
              fileUploadsByField={fileUploadsByField}
              onFileAdd={handleFileAdd}
              onFileRemove={handleFileRemove}
              onFileRetry={handleFileRetry}
            />
            {section?.image != null && <Image data={section.image} />}
          </div>
        ))}

        <div className="form-submit">
          <Button
            type="submit"
            text="Submit"
            theme="base"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
        </div>

        {submitMessage && (
          <div className={`form-message ${submitMessage.type}`}>{submitMessage.text}</div>
        )}
      </form>
    </DynamicFormWrapper>
  );
}

export default DynamicFormView;
